// shell/panes/entity-pane-actions.ts
// Pure planner for an entity pane's title-row actions (actionsPlacement: 'pane').
// Kept free of DOM so the rules are unit-testable in node; `entity-pane.ts`
// renders the plan with `renderPaneActions`.

import type { EntityViewMode } from '../components/entity-view/types';
import type { EntityPaneConfig, EntityAction, EntityActionContext } from './entity-pane';
import type { PaneActionDescriptor } from './pane-actions';

/** What the planner needs to know about the live EntityView. */
export interface EntityPaneActionSource<T> {
    mode: EntityViewMode;
    model: T | null;
    /** Whether an edit form exists at all (no form ⇒ no Edit). */
    hasForm: boolean;
    isDirty: boolean;
}

/** What each planned action does when clicked. */
export interface EntityPaneActionHandlers<T> {
    edit: () => void;
    delete: () => void;
    save: () => void;
    cancel: () => void;
    run: (action: EntityAction<T>) => void;
}

/**
 * Check if edit is allowed for a model.
 */
export function canEdit<T>(config: EntityPaneConfig<T>, model: T): boolean {
    if (config.canEdit === undefined) return false;
    if (typeof config.canEdit === 'boolean') return config.canEdit;
    return config.canEdit(model);
}

/**
 * Check if delete is allowed for a model.
 */
export function canDelete<T>(config: EntityPaneConfig<T>, model: T): boolean {
    if (config.canDelete === undefined) return false;
    if (typeof config.canDelete === 'boolean') return config.canDelete;
    return config.canDelete(model);
}

function submitLabel<T>(config: EntityPaneConfig<T>, mode: 'edit' | 'new'): string {
    if (typeof config.submitLabel === 'function') return config.submitLabel(mode);
    if (config.submitLabel) return config.submitLabel;
    return mode === 'new' ? 'Create' : 'Save';
}

/**
 * Plan the title-row actions for an entity pane.
 *
 * View mode: custom actions scoped to 'view' (an action whose `visible`
 * returns false is omitted outright; `canExecute` only disables), then Edit
 * (form + canEdit), then Delete (onDelete + canDelete, named and placed by
 * `config.delete`). Edit/new mode: custom actions scoped to that
 * mode, then Cancel, then the commit (Save / Create / `submitLabel`), then — in
 * edit mode only — Delete. The commit is disabled while an edit form is clean
 * and whenever `submitBlockedReason` returns a reason; the reason becomes the
 * tooltip. Nothing is planned until the model has loaded.
 */
export function planEntityPaneActions<T>(
    config: EntityPaneConfig<T>,
    src: EntityPaneActionSource<T>,
    actionCtx: EntityActionContext<T>,
    handlers: EntityPaneActionHandlers<T>,
): PaneActionDescriptor[] {
    const model = src.model;
    if (model === null || model === undefined) return [];
    const out: PaneActionDescriptor[] = [];

    for (const action of config.actions ?? []) {
        if (action.modes && !action.modes.includes(src.mode)) continue;
        if (action.visible && !action.visible(model, actionCtx)) continue;
        const enabled = action.canExecute ? action.canExecute(model, actionCtx) : true;
        out.push({
            key: action.key,
            label: action.label,
            variant: action.variant,
            overflow: action.overflow,
            disabled: !enabled,
            title: !enabled ? action.disabledReason?.(model, actionCtx) : undefined,
            execute: () => handlers.run(action),
        });
    }

    // The key and the danger variant are fixed; the label and whether it folds
    // into the ⋯ menu come from `config.delete` (defaults: "Delete", inline).
    const deleteAction = (): PaneActionDescriptor | null =>
        config.onDelete && canDelete(config, model)
            ? {
                key: 'delete',
                label: config.delete?.label ?? 'Delete',
                variant: 'danger',
                overflow: config.delete?.overflow,
                execute: handlers.delete,
            }
            : null;

    if (src.mode === 'view') {
        if (src.hasForm && canEdit(config, model)) {
            out.push({ key: 'edit', label: 'Edit', execute: handlers.edit });
        }
        const del = deleteAction();
        if (del) out.push(del);
        return out;
    }

    out.push({ key: 'cancel', label: config.cancelLabel ?? 'Cancel', execute: handlers.cancel });

    const label = submitLabel(config, src.mode);
    const blocked = config.submitBlockedReason?.(model, actionCtx);
    const clean = src.mode === 'edit' && !src.isDirty;
    out.push({
        key: 'save',
        label,
        variant: 'primary',
        disabled: !!blocked || clean,
        title: blocked ?? (clean ? 'No changes to save' : `${label} (Ctrl+Enter)`),
        execute: handlers.save,
    });

    if (src.mode === 'edit') {
        const del = deleteAction();
        if (del) out.push(del);
    }
    return out;
}
